'use client';

import { reCAPTCHA } from '@/api/account';
import Dialog from '@/components/Dialog';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting';
import { env } from '@/utils/env';
import { initTongDunScript } from '@/utils/tongdunScript';
import { randomString } from '@/utils/tools';
import { CloseOutlined } from '@zeelool/icon';
import React, { useEffect, useRef, useState } from 'react';

// 声明全局的 grecaptcha 对象类型
declare global {
  interface Window {
    grecaptcha: {
      render: (element: string, options: any) => number;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId: number) => string;
    };
  }
}

interface ReCaptchaDialogProps {
  open: boolean;
  action?: string;
  onSuccess: (token: string) => void;
  onExpired?: () => void;
  onError?: () => void;
  onClose: () => void;
}

const ReCaptchaDialog: React.FC<ReCaptchaDialogProps> = ({
  open,
  action = 'signup',
  onSuccess,
  onExpired,
  onError,
  onClose,
}) => {
  const [widgetId, setWidgetId] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isRenderingRef = useRef(false);
  const containerIdRef = useRef<string>('');
  const { trackEvent } = useReporting();

  // 埋点曝光
  const trackCaptchaCheckPopUpExp = () => {
    try {
      trackEvent(
        {
          eventName: 'CaptchaCheckPopUp_Exp',
          properties: {
            popup_type: '人机验证弹窗',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch {
      // console.warn('埋点触发失败:', error);
    }
  };

  // 埋点验证结果
  const trackCaptchaCheckResult = (isSuccess: boolean) => {
    try {
      trackEvent(
        {
          eventName: 'CaptchaCheck_Result',
          properties: {
            is_verify_success: isSuccess ? 'success' : 'fail',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch {
      // console.warn('埋点触发失败:', error);
    }
  };

  // 验证成功处理
  const handleSuccess = async (token: string) => {
    try {
      // 调用验证接口
      const { data } = await reCAPTCHA({ code: token });
      if (data.verified) {
        trackCaptchaCheckResult(true);
        onSuccess(token);
      } else {
        trackCaptchaCheckResult(false);
        onError?.();
      }
    } catch {
      onError?.();
    }
  };

  // 验证过期处理
  const handleExpired = () => {
    onExpired?.();
  };

  // 验证错误处理
  const handleError = () => {
    onError?.();
  };

  // 清理函数
  const cleanup = () => {
    try {
      // 重置widget
      if (widgetId !== null && window.grecaptcha?.reset) {
        try {
          window.grecaptcha.reset(widgetId);
        } catch {
          //   console.warn('reCAPTCHA reset失败:', resetError);
        }
      }

      // 清理容器
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      setWidgetId(null);
      isRenderingRef.current = false;
    } catch {
      //   console.warn('cleanup失败:', error);
    }
  };

  // 渲染reCAPTCHA
  const renderReCaptcha = () => {
    if (isRenderingRef.current) {
      return;
    }

    if (!window.grecaptcha?.render) {
      return;
    }

    if (!containerRef.current) {
      return;
    }

    isRenderingRef.current = true;

    try {
      // 确保容器是空的
      containerRef.current.innerHTML = '';

      // 设置容器ID
      const containerId = containerIdRef.current;
      containerRef.current.id = containerId;

      // 渲染reCAPTCHA
      const newWidgetId = window.grecaptcha.render(containerId, {
        sitekey: env.NEXT_PUBLIC_TONG_DUN_SITE_KEY,
        callback: handleSuccess,
        action,
        'expired-callback': handleExpired,
        'error-callback': handleError,
      });

      setWidgetId(newWidgetId);
    } catch {
      // 不需处理
    } finally {
      isRenderingRef.current = false;
    }
  };

  // 处理弹窗关闭
  const handleClose = () => {
    cleanup();
    onClose();
  };

  // 当对话框打开时的处理
  useEffect(() => {
    if (open) {
      // 生成唯一容器ID
      containerIdRef.current = `recaptcha-${randomString(16)}-${Date.now()}`;

      // 触发埋点
      trackCaptchaCheckPopUpExp();

      // 延迟渲染，确保DOM准备好
      const timer = setTimeout(() => {
        renderReCaptcha();
      }, 500);

      return () => {
        clearTimeout(timer);
      };
    } else {
      // 关闭时清理
      cleanup();
    }
  }, [open]); // 只依赖open状态

  // 延迟加载脚本（reCAPTCHA 和同盾），避免影响 TBT
  // 注意：即使使用 requestIdleCallback，脚本执行时仍会阻塞主线程
  // 因此需要确保在页面加载完成后再执行，避免影响 TBT（TBT 计算窗口通常是页面加载的前 5 秒）
  useEffect(() => {
    setTimeout(() => {
      initTongDunScript().catch(() => {
        // 静默处理错误，不影响用户体验
      });
    }, 5000);
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={false}
        showCloseIcon={false}
        footer={null}
        title={null}
        sx={{
          '& .MuiPaper-root': {
            width: {
              xs: '80vw',
              md: '400px',
            },
          },
          '& .MuiDialogTitle-root': {
            padding: '0px',
          },
          '& .MuiDialogContent-root': {
            padding: {
              xs: '20px 20px 20px 20px',
              md: '20px 20px 32px 20px',
            },
          },
        }}
      >
        <div className="">
          <div className="py-[20px] relative">
            <h3 className="text-[16px] md:text-[18px] font-bb text-center">Security Check</h3>
            <div className="absolute right-[0] top-[0] cursor-pointer" onClick={handleClose}>
              <CloseOutlined className="text-[20px] md:text-[28px]" />
            </div>
          </div>

          <div className="">
            <div className="google-recaptcha-container flex justify-center items-center transform scale-80 origin-center md:scale-100">
              <div ref={containerRef} className="g-recaptcha" />
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ReCaptchaDialog;
